import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { IntakeServiceSummary } from '../types';

interface ServiceProgressRingProps {
  service: IntakeServiceSummary;
  size?: number;
}

export function ServiceProgressRing({ service, size = 62 }: ServiceProgressRingProps) {
  const theme = useTheme();
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(100, service.progress));
  const stroke =
    service.accent === 'secondary'
      ? theme.palette.secondary.main
      : service.accent === 'success'
        ? theme.palette.success.main
        : service.accent === 'warning'
          ? theme.palette.warning.main
          : theme.palette.primary.main;
  const inactiveStroke = alpha(theme.palette.text.secondary, 0.22);

  return (
    <Box sx={{ minWidth: 82, textAlign: 'center', opacity: service.active ? 1 : 0.62 }}>
      <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={alpha(theme.palette.primary.main, 0.12)}
            strokeWidth={5}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={service.active ? stroke : inactiveStroke}
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - progress / 100)}
            strokeLinecap="round"
            strokeWidth={5}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {progress}%
          </Typography>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 800, lineHeight: 1.2 }}>
        {service.label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {service.active ? service.description : 'Available'}
      </Typography>
    </Box>
  );
}
